import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navItems, site } from "@/config/site";
import { searchArticles } from "@/lib/content";

export default function SiteHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  const onHero = pathname === "/" && !scrolled;
  const results = keyword.trim() ? searchArticles(keyword).slice(0, 5) : [];

  const handleJump = (id: string) => {
    setSearchOpen(false);
    setKeyword("");
    setMenuOpen(false);
    navigate(`/article/${id}`);
  };

  const iconBtn = onHero
    ? "border-background-50/30 bg-background-50/10 text-background-50 hover:border-background-50/60"
    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        onHero
          ? "bg-transparent border-b border-transparent"
          : "bg-background-50/95 border-b border-background-200 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-water-flash-line text-lg md:text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-black text-lg md:text-xl tracking-wide ${
                  onHero ? "text-background-50" : "text-foreground-950"
                }`}
              >
                {site.name}
              </span>
              <span
                className={`hidden sm:block text-[10px] md:text-[11px] tracking-[0.2em] mt-0.5 ${
                  onHero ? "text-background-50/70" : "text-foreground-500"
                }`}
              >
                DAILY BUZZ
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={() => {
                  const base = "px-4 py-2 text-sm rounded-full transition-colors cursor-pointer";
                  if (onHero) {
                    return `${base} text-background-50/85 hover:text-background-50 hover:bg-background-50/15`;
                  }
                  return `${base} text-foreground-700 hover:text-primary-600 hover:bg-background-100`;
                }}
              >
                {({ isActive }) => (
                  <span
                    className={
                      isActive
                        ? onHero
                          ? "text-background-50 font-medium"
                          : "text-primary-700 font-medium"
                        : undefined
                    }
                  >
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              aria-label="搜索瓜田"
              onClick={() => setSearchOpen((v) => !v)}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors cursor-pointer ${iconBtn}`}
            >
              <i className={searchOpen ? "ri-close-line" : "ri-search-line"}></i>
            </button>
            <Link
              to="/#subscribe"
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-notification-3-line"></i>
              订阅新瓜
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-10 h-10 rounded-full border flex items-center justify-center cursor-pointer ${iconBtn}`}
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="w-full bg-background-50 border-t border-background-200">
          <div className="w-full px-4 md:px-8 py-4">
            <div className="max-w-content mx-auto">
              <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-background-300 bg-background-100">
                <i className="ri-search-2-line text-foreground-500 text-lg"></i>
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索今天有什么瓜，例如：新专辑、点映、Livehouse"
                  className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={() => handleJump(results[0]?.id ?? "")}
                  disabled={results.length === 0}
                  className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium disabled:opacity-40 cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </div>
              {results.length > 0 && (
                <ul className="mt-3 divide-y divide-background-200 rounded-md border border-background-200 overflow-hidden">
                  {results.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleJump(item.id)}
                        className="w-full text-left px-4 py-3 bg-background-50 hover:bg-primary-50 transition-colors flex items-center justify-between gap-4 cursor-pointer"
                      >
                        <span className="text-sm text-foreground-800 line-clamp-1">
                          {item.title}
                        </span>
                        <span className="text-xs text-primary-600 whitespace-nowrap">
                          去看看
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {keyword.trim() && results.length === 0 && (
                <p className="mt-3 text-sm text-foreground-500">
                  暂时没搜到相关瓜，换个关键词试试吧。
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden w-full bg-background-50 border-t border-background-200">
          <nav className="px-4 py-3 flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm flex items-center justify-between ${
                    isActive
                      ? "bg-primary-100 text-primary-700 font-medium"
                      : "text-foreground-700"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </NavLink>
            ))}
            <Link
              to="/#subscribe"
              onClick={() => setMenuOpen(false)}
              className="mt-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium text-center"
            >
              订阅新瓜早报
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}